import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { ArrowLeft, CreditCard, Building2, Smartphone, ClipboardCheck, Link2, Wallet } from 'lucide-react';
import { MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE } from '@masonic-tools/shared-auth';
import {
  DEFAULT_SLICKPAY_NETWORK_SETTINGS,
  resolveSlickpayNetworkSettings,
  type SlickpayNetworkSettings,
} from '@/lib/slickpayNetwork';

export interface SystemSettings {
  maintenanceMode: boolean;
  currentVersion: string;
  featureFlags: Record<string, boolean>;
  requireUnitReview?: boolean;
  
  creditsSystemMode?: 'enabled' | 'disabled' | 'admin_only';

  /** Whole credits set on new unit documents when created from apps (0 to max, enforced by rules). */
  defaultCreditBalanceOnUnitCreate?: number;

  /** How units buy credit packs: Stripe Checkout or SlickPay Open Banking */
  creditsPaymentProvider?: 'stripe' | 'slickpay';

  /** Payment links (charity collections) */
  paymentLinks?: {
    /** Unit credits debited for each completed payment-link payment (default 1). */
    creditsPerPayment?: number;
  };

  
  /** @deprecated Use paymentMethods instead */
  transactionFee?: number;
  
  /** @deprecated Use paymentMethods.stripe.enabled instead */
  processPaymentViaCard?: boolean;
  
  /** Payment methods configuration with per-method fees */
  paymentMethods?: {
    bankTransfer: {
      enabled: boolean;
      transactionFee: number;
    };
    stripe: {
      enabled: boolean;
      transactionFee: number;
    };
    sumup: {
      enabled: boolean;
      transactionFee: number;
    };
    slickpay: {
      enabled: boolean;
      transactionFee: number;
    };
  };
}

export default function PlatformSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Payment method states
  const [bankTransferEnabled, setBankTransferEnabled] = useState(true);
  const [bankTransferFee, setBankTransferFee] = useState('0.00');
  
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [stripeFee, setStripeFee] = useState('1.00');
  
  const [sumupEnabled, setSumupEnabled] = useState(false);
  const [sumupFee, setSumupFee] = useState('1.00');

  const [slickpayEnabled, setSlickpayEnabled] = useState(false);
  const [slickpayFee, setSlickpayFee] = useState('1.00');
  
  const [requireUnitReview, setRequireUnitReview] = useState(true);
  const [maToBearerToken, setMaToBearerToken] = useState('');
  const [savingToken, setSavingToken] = useState(false);
  const [tokenConfigured, setTokenConfigured] = useState(false);
  const [tokenLastFour, setTokenLastFour] = useState('');

  const [creditsSystemMode, setCreditsSystemMode] = useState<'enabled' | 'disabled' | 'admin_only'>('enabled');
  const [savingCreditsMode, setSavingCreditsMode] = useState(false);

  const [creditsPaymentProvider, setCreditsPaymentProvider] = useState<'stripe' | 'slickpay'>('stripe');
  const [paymentLinkCreditsInput, setPaymentLinkCreditsInput] = useState('1');
  const [savingPaymentLinkCredits, setSavingPaymentLinkCredits] = useState(false);
  const [savingCreditsProvider, setSavingCreditsProvider] = useState(false);

  const [defaultCreditBalanceOnUnitCreateInput, setDefaultCreditBalanceOnUnitCreateInput] = useState('0');
  const [savingDefaultStartingCredits, setSavingDefaultStartingCredits] = useState(false);

  const [stripeCreditSecretKey, setStripeCreditSecretKey] = useState('');
  const [stripeCreditWebhookSecret, setStripeCreditWebhookSecret] = useState('');
  const [stripeCreditKeyLastFour, setStripeCreditKeyLastFour] = useState('');
  const [stripeCreditWebhookLastFour, setStripeCreditWebhookLastFour] = useState('');
  const [savingStripeCreditCreds, setSavingStripeCreditCreds] = useState(false);

  const [slickpayApplicationId, setSlickpayApplicationId] = useState('');
  const [slickpayApplicationSecret, setSlickpayApplicationSecret] = useState('');
  const [slickpayApiConfigured, setSlickpayApiConfigured] = useState(false);
  const [slickpaySavedApplicationId, setSlickpaySavedApplicationId] = useState('');
  const [slickpayCreditNameOnAccount, setSlickpayCreditNameOnAccount] = useState('');
  const [slickpayCreditSortCode, setSlickpayCreditSortCode] = useState('');
  const [slickpayCreditAccountNumber, setSlickpayCreditAccountNumber] = useState('');
  const [slickpayCreditConfigured, setSlickpayCreditConfigured] = useState(false);
  const [savingSlickpayCreditCreds, setSavingSlickpayCreditCreds] = useState(false);
  const [diagnosingSlickpay, setDiagnosingSlickpay] = useState(false);
  const [slickpayDiagnosticReport, setSlickpayDiagnosticReport] = useState('');
  const [slickpayNetwork, setSlickpayNetwork] = useState<SlickpayNetworkSettings>(
    DEFAULT_SLICKPAY_NETWORK_SETTINGS,
  );

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data() as SystemSettings;
          
          // Load unit review setting
          setRequireUnitReview(data.requireUnitReview ?? true);

          setCreditsSystemMode(data.creditsSystemMode ?? 'enabled');
          setPaymentLinkCreditsInput(
            String(typeof data.paymentLinks?.creditsPerPayment === 'number' ? data.paymentLinks.creditsPerPayment : 1),
          );
          setCreditsPaymentProvider(data.creditsPaymentProvider === 'slickpay' ? 'slickpay' : 'stripe');
          setDefaultCreditBalanceOnUnitCreateInput(
            String(
              typeof data.defaultCreditBalanceOnUnitCreate === 'number'
                ? data.defaultCreditBalanceOnUnitCreate
                : 0,
            ),
          );
          
          // Check if we have the new payment methods structure
          if (data.paymentMethods) {
            // Use new structure
            setBankTransferEnabled(data.paymentMethods.bankTransfer?.enabled ?? true);
            setBankTransferFee(data.paymentMethods.bankTransfer?.transactionFee?.toFixed(2) ?? '0.00');
            
            setStripeEnabled(data.paymentMethods.stripe?.enabled ?? true);
            setStripeFee(data.paymentMethods.stripe?.transactionFee?.toFixed(2) ?? '1.00');
            
            setSumupEnabled(data.paymentMethods.sumup?.enabled ?? false);
            setSumupFee(data.paymentMethods.sumup?.transactionFee?.toFixed(2) ?? '1.00');

            setSlickpayEnabled(data.paymentMethods.slickpay?.enabled ?? false);
            setSlickpayFee(data.paymentMethods.slickpay?.transactionFee?.toFixed(2) ?? '1.00');
          } else {
            // Migrate from old structure
            console.log('Migrating from old payment settings structure');
            
            setBankTransferEnabled(true); // Always enable bank transfer
            setBankTransferFee('0.00'); // Bank transfer typically has no fee
            
            setStripeEnabled(data.processPaymentViaCard ?? true);
            setStripeFee(data.transactionFee?.toFixed(2) ?? '1.00');
            
            setSumupEnabled(false); // New method, default to disabled
            setSumupFee('1.00');
            setSlickpayEnabled(false);
            setSlickpayFee('1.00');
            
            // Auto-save the migrated structure
            const migratedSettings: SystemSettings = {
              ...data,
              paymentMethods: {
                bankTransfer: { enabled: true, transactionFee: 0 },
                stripe: { enabled: data.processPaymentViaCard ?? true, transactionFee: data.transactionFee ?? 1.00 },
                sumup: { enabled: false, transactionFee: 1.00 },
                slickpay: { enabled: false, transactionFee: 1.00 },
              },
              creditsPaymentProvider: 'stripe',
            };
            await setDoc(doc(db, 'systemSettings', 'platform'), migratedSettings);
            showSuccess('Payment settings migrated to new format');
          }
        } else {
          // Initialize with default settings
          const defaultSettings: SystemSettings = {
            maintenanceMode: false,
            currentVersion: '1.0.0',
            featureFlags: {},
            requireUnitReview: true,
            creditsPaymentProvider: 'stripe',
            paymentMethods: {
              bankTransfer: { enabled: true, transactionFee: 0 },
              stripe: { enabled: true, transactionFee: 1.00 },
              sumup: { enabled: false, transactionFee: 1.00 },
              slickpay: { enabled: false, transactionFee: 1.00 },
            },
          };
          
          await setDoc(doc(db, 'systemSettings', 'platform'), defaultSettings);
          
          setRequireUnitReview(true);
          setBankTransferEnabled(true);
          setBankTransferFee('0.00');
          setStripeEnabled(true);
          setStripeFee('1.00');
          setSumupEnabled(false);
          setSumupFee('1.00');
          setSlickpayEnabled(false);
          setSlickpayFee('1.00');
          setCreditsPaymentProvider('stripe');
          setDefaultCreditBalanceOnUnitCreateInput('0');
        }

        const tokenDoc = await getDoc(doc(db, 'admin', 'integrations', 'settings', 'mato'));
        if (tokenDoc.exists()) {
          const tokenData = tokenDoc.data() as { tokenLastFour?: string };
          if (tokenData.tokenLastFour) {
            setTokenConfigured(true);
            setTokenLastFour(tokenData.tokenLastFour);
          }
        }

        const stripeCreditDoc = await getDoc(
          doc(db, 'admin', 'integrations', 'settings', 'platformStripeCredits')
        );
        if (stripeCreditDoc.exists()) {
          const s = stripeCreditDoc.data() as { keyLastFour?: string; webhookLastFour?: string };
          if (s.keyLastFour) setStripeCreditKeyLastFour(s.keyLastFour);
          if (s.webhookLastFour) setStripeCreditWebhookLastFour(s.webhookLastFour);
        }

        const slickApiDoc = await getDoc(
          doc(db, 'admin', 'integrations', 'settings', 'platformSlickpayCredits')
        );
        if (slickApiDoc.exists()) {
          const api = slickApiDoc.data() as { applicationId?: string };
          if (api.applicationId?.trim()) {
            setSlickpayApiConfigured(true);
            setSlickpaySavedApplicationId(api.applicationId.trim());
          }
        }

        const slickCreditDoc = await getDoc(
          doc(db, 'admin', 'integrations', 'settings', 'platformSlickpayCreditor')
        );
        if (slickCreditDoc.exists()) {
          const s = slickCreditDoc.data() as {
            nameOnAccount?: string;
            sortCode?: string;
            accountNumber?: string;
          };
          if (s.nameOnAccount) setSlickpayCreditConfigured(true);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        showError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handlePaymentMethodToggle = async (
    method: 'bankTransfer' | 'stripe' | 'sumup' | 'slickpay',
    enabled: boolean
  ) => {
    // Update local state
    if (method === 'bankTransfer') setBankTransferEnabled(enabled);
    if (method === 'stripe') setStripeEnabled(enabled);
    if (method === 'sumup') setSumupEnabled(enabled);
    if (method === 'slickpay') setSlickpayEnabled(enabled);
    
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() 
        ? settingsDoc.data() as SystemSettings
        : {
            maintenanceMode: false,
            currentVersion: '1.0.0',
            featureFlags: {},
            paymentMethods: {
              bankTransfer: { enabled: true, transactionFee: 0 },
              stripe: { enabled: true, transactionFee: 1.00 },
              sumup: { enabled: false, transactionFee: 1.00 },
              slickpay: { enabled: false, transactionFee: 1.00 },
            },
          };

      if (!currentSettings.paymentMethods) {
        currentSettings.paymentMethods = {
          bankTransfer: { enabled: true, transactionFee: 0 },
          stripe: { enabled: true, transactionFee: 1.00 },
          sumup: { enabled: false, transactionFee: 1.00 },
          slickpay: { enabled: false, transactionFee: 1.00 },
        };
      }

      if (!currentSettings.paymentMethods.slickpay) {
        currentSettings.paymentMethods.slickpay = { enabled: false, transactionFee: 1.0 };
      }

      // Update the specific method
      currentSettings.paymentMethods[method].enabled = enabled;

      await setDoc(doc(db, 'systemSettings', 'platform'), currentSettings);
      const methodLabel =
        method === 'bankTransfer'
          ? 'Bank Transfer'
          : method === 'stripe'
            ? 'Stripe'
            : method === 'sumup'
              ? 'SumUp'
              : 'SlickPay';
      showSuccess(`${methodLabel} ${enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error saving payment method setting:', error);
      showError('Failed to save payment method setting');
      // Revert the toggle state on error
      if (method === 'bankTransfer') setBankTransferEnabled(!enabled);
      if (method === 'stripe') setStripeEnabled(!enabled);
      if (method === 'sumup') setSumupEnabled(!enabled);
      if (method === 'slickpay') setSlickpayEnabled(!enabled);
    }
  };

  const handleToggleUnitReview = async (enabled: boolean) => {
    setRequireUnitReview(enabled);
    
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() 
        ? settingsDoc.data() as SystemSettings
        : {
            maintenanceMode: false,
            currentVersion: '1.0.0',
            featureFlags: {},
            requireUnitReview: true,
            paymentMethods: {
              bankTransfer: { enabled: true, transactionFee: 0 },
              stripe: { enabled: true, transactionFee: 1.00 },
              sumup: { enabled: false, transactionFee: 1.00 },
              slickpay: { enabled: false, transactionFee: 1.00 },
            },
          };

      await setDoc(doc(db, 'systemSettings', 'platform'), {
        ...currentSettings,
        requireUnitReview: enabled,
      });
      showSuccess(`Unit review requirement ${enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error saving unit review setting:', error);
      showError('Failed to save unit review setting');
      setRequireUnitReview(!enabled);
    }
  };

  const handleSaveDefaultCreditBalanceOnUnitCreate = async () => {
    const n = Number(defaultCreditBalanceOnUnitCreateInput.trim());
    if (!Number.isInteger(n) || n < 0) {
      showError('Enter a whole number from 0 upwards.');
      return;
    }
    if (n > MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE) {
      showError(`Maximum is ${MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE.toLocaleString('en-GB')} credits.`);
      return;
    }
    setSavingDefaultStartingCredits(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() ? (settingsDoc.data() as Record<string, unknown>) : {};
      await setDoc(doc(db, 'systemSettings', 'platform'), {
        ...currentSettings,
        defaultCreditBalanceOnUnitCreate: n,
      });
      setDefaultCreditBalanceOnUnitCreateInput(String(n));
      showSuccess('Starting credits for new units saved.');
    } catch (error) {
      console.error('Error saving default starting credits:', error);
      showError('Failed to save starting credits for new units.');
    } finally {
      setSavingDefaultStartingCredits(false);
    }
  };

  const handleSaveCreditsSystemMode = async (mode: 'enabled' | 'disabled' | 'admin_only') => {
    setCreditsSystemMode(mode);
    setSavingCreditsMode(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      await setDoc(doc(db, 'systemSettings', 'platform'), {
        ...currentSettings,
        creditsSystemMode: mode,
      });
      showSuccess(`Credits system mode updated to ${mode.replace('_', ' ')}!`);
    } catch (error) {
      console.error('Error saving credits mode:', error);
      showError('Failed to save credits system mode');
    } finally {
      setSavingCreditsMode(false);
    }
  };

  const handleSavePaymentLinkCredits = async () => {
    const value = Number(paymentLinkCreditsInput);
    if (!Number.isInteger(value) || value < 0 || value > 100) {
      showError('Enter a whole number from 0 to 100');
      return;
    }
    setSavingPaymentLinkCredits(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      await setDoc(doc(db, 'systemSettings', 'platform'), {
        ...currentSettings,
        paymentLinks: { ...(currentSettings.paymentLinks || {}), creditsPerPayment: value },
      });
      showSuccess(value === 0 ? 'Payment link payments no longer use credits.' : `Each payment link payment now uses ${value} credit${value === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Error saving payment link credits:', error);
      showError('Failed to save payment link credits');
    } finally {
      setSavingPaymentLinkCredits(false);
    }
  };

  const handleSaveCreditsPaymentProvider = async (provider: 'stripe' | 'slickpay') => {
    const previous = creditsPaymentProvider;
    setCreditsPaymentProvider(provider);
    setSavingCreditsProvider(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      await setDoc(doc(db, 'systemSettings', 'platform'), {
        ...currentSettings,
        creditsPaymentProvider: provider,
      });
      showSuccess(
        provider === 'stripe'
          ? 'Credit purchases now use Stripe.'
          : 'Credit purchases now use SlickPay.'
      );
    } catch (error) {
      console.error('Error saving credits payment provider:', error);
      setCreditsPaymentProvider(previous);
      showError('Failed to save credit purchase provider');
    } finally {
      setSavingCreditsProvider(false);
    }
  };

  const handleSaveStripeCreditCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    const sk = stripeCreditSecretKey.trim();
    const wh = stripeCreditWebhookSecret.trim();
    if (!sk && !wh) {
      showError('Enter a Stripe secret key and/or a webhook signing secret to save');
      return;
    }
    setSavingStripeCreditCreds(true);
    try {
      const fn = httpsCallable(functions, 'savePlatformStripeCreditCredentials');
      const res = await fn({ secretKey: sk || undefined, webhookSecret: wh || undefined });
      const data = res.data as { keyLastFour?: string; webhookLastFour?: string };
      if (data?.keyLastFour) setStripeCreditKeyLastFour(data.keyLastFour);
      if (data?.webhookLastFour) setStripeCreditWebhookLastFour(data.webhookLastFour);
      setStripeCreditSecretKey('');
      setStripeCreditWebhookSecret('');
      showSuccess('Stripe credentials for credit purchases saved');
    } catch (error) {
      console.error(error);
      showError('Failed to save Stripe credentials');
    } finally {
      setSavingStripeCreditCreds(false);
    }
  };

  const handleSaveSlickpayCreditCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    const applicationId = slickpayApplicationId.trim();
    const applicationSecret = slickpayApplicationSecret.trim();
    const nameOnAccount = slickpayCreditNameOnAccount.trim();
    const sortCode = slickpayCreditSortCode.replace(/[-\s]/g, '');
    const accountNumber = slickpayCreditAccountNumber.replace(/[-\s]/g, '');

    const savingApi = !!(applicationId || applicationSecret);
    const savingBank = !!(nameOnAccount || sortCode || accountNumber);

    if (!savingApi && !savingBank) {
      showError('Enter Hope Macy API credentials and/or creditor bank account details');
      return;
    }
    if (savingApi && (!applicationId || !applicationSecret)) {
      showError('Both Application ID and Application Secret are required');
      return;
    }
    if (savingBank) {
      if (!nameOnAccount) {
        showError('Enter the account name');
        return;
      }
      if (!/^\d{6}$/.test(sortCode)) {
        showError('Sort code must be 6 digits (strip any hyphens)');
        return;
      }
      if (!/^\d{8}$/.test(accountNumber)) {
        showError('Account number must be 8 digits');
        return;
      }
    }

    setSavingSlickpayCreditCreds(true);
    try {
      const fn = httpsCallable(functions, 'savePlatformSlickpayCreditCredentials');
      const result = await fn({
        ...(savingApi ? { applicationId, applicationSecret } : {}),
        ...(savingBank ? { nameOnAccount, sortCode, accountNumber } : {}),
      });
      const data = result.data as { applicationIdSaved?: string };
      if (data?.applicationIdSaved) {
        setSlickpayApiConfigured(true);
        setSlickpaySavedApplicationId(data.applicationIdSaved);
        setSlickpayApplicationId('');
        setSlickpayApplicationSecret('');
      }
      if (savingBank) {
        setSlickpayCreditConfigured(true);
        setSlickpayCreditNameOnAccount('');
        setSlickpayCreditSortCode('');
        setSlickpayCreditAccountNumber('');
      }
      showSuccess('Open Banking settings saved');
    } catch (error) {
      console.error(error);
      showError('Failed to save Open Banking settings');
    } finally {
      setSavingSlickpayCreditCreds(false);
    }
  };

  const handleRunSlickPayDiagnostic = async () => {
    setDiagnosingSlickpay(true);
    setSlickpayDiagnosticReport('');
    try {
      const fn = httpsCallable<
        Record<string, never>,
        {
          supportReport: string;
          egressIpMatch: boolean | null;
          createSessionOk: boolean;
          expectedEgressIp?: string;
          observedEgressIp?: string | null;
          vpcConnector?: string;
          creditorAccountCoverage?: Array<{ label: string; status: string }>;
        }
      >(functions, 'diagnoseSlickPaySetup');
      const res = await fn({});
      setSlickpayDiagnosticReport(res.data.supportReport);
      setSlickpayNetwork(
        resolveSlickpayNetworkSettings({
          expectedEgressIp: res.data.expectedEgressIp,
          ipWhitelist: res.data.expectedEgressIp
            ? [res.data.expectedEgressIp]
            : DEFAULT_SLICKPAY_NETWORK_SETTINGS.ipWhitelist,
          vpcConnectorName: res.data.vpcConnector,
        }),
      );
      const unregisteredAccounts = (res.data.creditorAccountCoverage ?? []).filter(
        (row) => row.status !== 'registered',
      );
      if (res.data.createSessionOk && res.data.egressIpMatch && unregisteredAccounts.length > 0) {
        showError(
          `Connected, but ${unregisteredAccounts.length} Open Banking bank account(s) are not registered with Hope Macy. See report below.`,
        );
      } else if (res.data.createSessionOk && res.data.egressIpMatch) {
        showSuccess('Connectivity check passed — IP and credentials look correct.');
      } else if (res.data.egressIpMatch === false) {
        showError('Egress IP does not match the expected whitelist IP. See report below.');
      } else {
        showError('Hope Macy rejected credentials or connectivity check failed. Copy the report for Hope Macy support.');
      }
    } catch (error) {
      console.error(error);
      showError('Diagnostic check failed to run. Deploy diagnoseSlickPaySetup if this is a new function.');
    } finally {
      setDiagnosingSlickpay(false);
    }
  };

  const handleSavePaymentFees = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fees
    const btFee = parseFloat(bankTransferFee);
    const stFee = parseFloat(stripeFee);
    const suFee = parseFloat(sumupFee);
    const spFee = parseFloat(slickpayFee);

    if (
      isNaN(btFee) ||
      btFee < 0 ||
      isNaN(stFee) ||
      stFee < 0 ||
      isNaN(suFee) ||
      suFee < 0 ||
      isNaN(spFee) ||
      spFee < 0
    ) {
      showError('Please enter valid transaction fees (must be 0 or positive)');
      return;
    }

    if (btFee > 100 || stFee > 100 || suFee > 100 || spFee > 100) {
      showError('Transaction fees cannot exceed £100');
      return;
    }

    setSaving(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() 
        ? settingsDoc.data() as SystemSettings
        : {
            maintenanceMode: false,
            currentVersion: '1.0.0',
            featureFlags: {},
            paymentMethods: {
              bankTransfer: { enabled: true, transactionFee: 0 },
              stripe: { enabled: true, transactionFee: 1.00 },
              sumup: { enabled: false, transactionFee: 1.00 },
              slickpay: { enabled: false, transactionFee: 1.00 },
            },
          };

      // Ensure paymentMethods exists
      if (!currentSettings.paymentMethods) {
        currentSettings.paymentMethods = {
          bankTransfer: { enabled: bankTransferEnabled, transactionFee: btFee },
          stripe: { enabled: stripeEnabled, transactionFee: stFee },
          sumup: { enabled: sumupEnabled, transactionFee: suFee },
          slickpay: { enabled: slickpayEnabled, transactionFee: spFee },
        };
      } else {
        if (!currentSettings.paymentMethods.slickpay) {
          currentSettings.paymentMethods.slickpay = { enabled: false, transactionFee: 1.0 };
        }
        // Update fees
        currentSettings.paymentMethods.bankTransfer.transactionFee = btFee;
        currentSettings.paymentMethods.stripe.transactionFee = stFee;
        currentSettings.paymentMethods.sumup.transactionFee = suFee;
        currentSettings.paymentMethods.slickpay.transactionFee = spFee;
      }

      await setDoc(doc(db, 'systemSettings', 'platform'), currentSettings);
      showSuccess('Payment method fees updated successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMaToToken = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedToken = maToBearerToken.trim();

    if (!trimmedToken) {
      showError('Please enter a bearer token before saving');
      return;
    }

    setSavingToken(true);
    try {
      const saveToken = httpsCallable(functions, 'saveMaToBearerToken');
      const response = await saveToken({ token: trimmedToken });
      const data = response.data as { tokenLastFour?: string };
      const lastFour = data?.tokenLastFour || trimmedToken.slice(-4);

      setTokenConfigured(true);
      setTokenLastFour(lastFour);
      setMaToBearerToken('');
      showSuccess('Ma-to bearer token saved securely');
    } catch (error) {
      console.error('Error saving ma-to token:', error);
      showError('Failed to save bearer token');
    } finally {
      setSavingToken(false);
    }
  };


  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Platform Settings</h1>
            <p className="text-muted-foreground">Configure global platform settings and fees</p>
          </div>

          <div className="space-y-6">
            {/* Unit Review Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5" />
                  Unit Review Settings
                </CardTitle>
                <CardDescription>
                  Control whether new units require admin review before being approved
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="requireUnitReview">Require Unit Review</Label>
                    <p className="text-sm text-muted-foreground">
                      {requireUnitReview 
                        ? 'New units will be submitted for admin review before approval'
                        : 'New units will be automatically approved upon creation'}
                    </p>
                  </div>
                  <Switch
                    id="requireUnitReview"
                    checked={requireUnitReview}
                    onCheckedChange={handleToggleUnitReview}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Credits System Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  Credits System Settings
                </CardTitle>
                <CardDescription>
                  Control who has access to the credits system across the platform.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-w-sm">
                  <Label htmlFor="creditsSystemMode">Credits System Mode</Label>
                  <select
                    id="creditsSystemMode"
                    className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={creditsSystemMode}
                    onChange={(e) => handleSaveCreditsSystemMode(e.target.value as any)}
                    disabled={savingCreditsMode}
                  >
                    <option value="enabled">Enabled (All Units)</option>
                    <option value="admin_only">Admin Only (Testing)</option>
                    <option value="disabled">Disabled (Hidden)</option>
                  </select>
                  <p className="text-sm text-muted-foreground mt-2">
                    {creditsSystemMode === 'enabled' && 'Credits system is active for all units.'}
                    {creditsSystemMode === 'admin_only' &&
                      'Only platform admins see credit balances and purchases. Confirmed RSVPs do not debit unit balances while this mode is active.'}
                    {creditsSystemMode === 'disabled' && 'Credits system is hidden entirely.'}
                  </p>
                </div>

                <div className="mt-6 max-w-md space-y-3 border-t pt-6">
                  <Label htmlFor="defaultCreditBalanceOnUnitCreate">Starting credits for new units</Label>
                  <p className="text-xs text-muted-foreground">
                    Whole number from 0 to {MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE.toLocaleString('en-GB')}. Applied
                    when a unit document is first created. Firestore rules require new units to match this value so
                    balances cannot be spoofed on create.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <Input
                      id="defaultCreditBalanceOnUnitCreate"
                      type="number"
                      min={0}
                      max={MAX_DEFAULT_CREDIT_BALANCE_ON_UNIT_CREATE}
                      step={1}
                      className="max-w-[200px]"
                      value={defaultCreditBalanceOnUnitCreateInput}
                      onChange={(e) => setDefaultCreditBalanceOnUnitCreateInput(e.target.value)}
                      disabled={savingDefaultStartingCredits}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleSaveDefaultCreditBalanceOnUnitCreate}
                      disabled={savingDefaultStartingCredits}
                    >
                      {savingDefaultStartingCredits ? 'Saving…' : 'Save starting credits'}
                    </Button>
                  </div>
                </div>

                <div className="mt-6 max-w-md space-y-3 border-t pt-6">
                  <Label htmlFor="paymentLinkCreditsPerPayment">Credits per payment-link payment</Label>
                  <p className="text-xs text-muted-foreground">
                    Credits taken from a unit each time someone pays one of its payment links (charity collections).
                    Set 0 to make payment links free. Not charged while the credits system is admin-only or disabled.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <Input
                      id="paymentLinkCreditsPerPayment"
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      className="max-w-[200px]"
                      value={paymentLinkCreditsInput}
                      onChange={(e) => setPaymentLinkCreditsInput(e.target.value)}
                      disabled={savingPaymentLinkCredits}
                    />
                    <Button type="button" variant="secondary" onClick={handleSavePaymentLinkCredits} disabled={savingPaymentLinkCredits}>
                      {savingPaymentLinkCredits ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Credit pack purchases (platform)
                </CardTitle>
                <CardDescription>
                  Choose how lodges buy Masonic Tools credits: Stripe card checkout or SlickPay Open Banking. This is
                  separate from per-unit guest payment methods below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="rounded-md border bg-muted/40 px-4 py-3 text-sm">
                  <p className="font-medium text-foreground">Credit pack catalogue</p>
                  <p className="text-muted-foreground mt-1">
                    Prices and labels for purchasable bundles are edited on the dedicated Credit packs page.
                  </p>
                  <Button type="button" variant="secondary" className="mt-3" onClick={() => navigate('/credit-packs')}>
                    Edit credit packs
                  </Button>
                </div>
                <div className="space-y-2 max-w-md">
                  <Label htmlFor="creditsPaymentProvider">Provider for credit purchases</Label>
                  <select
                    id="creditsPaymentProvider"
                    className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={creditsPaymentProvider}
                    onChange={(e) =>
                      handleSaveCreditsPaymentProvider(e.target.value as 'stripe' | 'slickpay')
                    }
                    disabled={savingCreditsProvider}
                  >
                    <option value="stripe">Stripe (card)</option>
                    <option value="slickpay">SlickPay (Open Banking)</option>
                  </select>
                  <p className="text-sm text-muted-foreground">
                    Treasurers and Secretaries see the matching checkout when they buy credits for a unit.
                  </p>
                </div>

                {creditsPaymentProvider === 'stripe' && (
                  <form onSubmit={handleSaveStripeCreditCredentials} className="space-y-4 max-w-xl border rounded-lg p-4">
                    <h3 className="text-sm font-semibold">Stripe configuration</h3>
                    <p className="text-xs text-muted-foreground">
                      Optional if you already set <code className="text-xs">STRIPE_SECRET_KEY</code> and{' '}
                      <code className="text-xs">STRIPE_PLATFORM_WEBHOOK_SECRET</code> in the Functions environment.
                      Values saved here override those for credit purchases and the platform Stripe webhook only.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="stripeCreditSecret">Stripe secret key (sk_live… or sk_test…)</Label>
                      <Input
                        id="stripeCreditSecret"
                        type="password"
                        autoComplete="new-password"
                        value={stripeCreditSecretKey}
                        onChange={(e) => setStripeCreditSecretKey(e.target.value)}
                        placeholder="Leave blank to skip updating the key"
                      />
                      {stripeCreditKeyLastFour ? (
                        <p className="text-xs text-muted-foreground">
                          Saved key ending in <span className="font-mono">{stripeCreditKeyLastFour}</span>
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="stripeCreditWebhook">Stripe webhook signing secret (whsec…)</Label>
                      <Input
                        id="stripeCreditWebhook"
                        type="password"
                        autoComplete="new-password"
                        value={stripeCreditWebhookSecret}
                        onChange={(e) => setStripeCreditWebhookSecret(e.target.value)}
                        placeholder="Endpoint: platform Stripe webhook for checkout.session.completed"
                      />
                      {stripeCreditWebhookLastFour ? (
                        <p className="text-xs text-muted-foreground">
                          Saved webhook secret ending in <span className="font-mono">{stripeCreditWebhookLastFour}</span>
                        </p>
                      ) : null}
                    </div>
                    <Button type="submit" disabled={savingStripeCreditCreds}>
                      {savingStripeCreditCreds ? 'Saving…' : 'Save Stripe credentials'}
                    </Button>
                  </form>
                )}

                {creditsPaymentProvider === 'slickpay' && (
                  <form onSubmit={handleSaveSlickpayCreditCredentials} className="space-y-4 max-w-xl border rounded-lg p-4">
                    <h3 className="text-sm font-semibold">Open Banking (SlickPay PIS v1)</h3>
                    <p className="text-xs text-muted-foreground">
                      Use your <strong>production</strong> Hope Macy (.com) Application ID and Secret. These are also
                      supported via Google Secret Manager if preferred.
                    </p>
                    {slickpayApiConfigured && (
                      <p className="rounded-md bg-green-50 px-3 py-2 text-xs text-green-700 border border-green-200">
                        API credentials configured
                        {slickpaySavedApplicationId ? (
                          <>
                            {' '}
                            (Application ID: <code className="text-xs">{slickpaySavedApplicationId}</code>)
                          </>
                        ) : null}
                        . Enter new values below to replace them.
                      </p>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="slickpayApplicationId">Hope Macy Application ID</Label>
                      <Input
                        id="slickpayApplicationId"
                        value={slickpayApplicationId}
                        onChange={(e) => setSlickpayApplicationId(e.target.value)}
                        placeholder="Production UUID from Hope Macy dashboard"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="slickpayApplicationSecret">Hope Macy Application Secret</Label>
                      <Input
                        id="slickpayApplicationSecret"
                        type="password"
                        autoComplete="new-password"
                        value={slickpayApplicationSecret}
                        onChange={(e) => setSlickpayApplicationSecret(e.target.value)}
                        placeholder="Production secret from Hope Macy dashboard"
                      />
                    </div>
                    <h4 className="text-sm font-medium pt-2">Creditor bank account (credit-pack payouts)</h4>
                    <p className="text-xs text-muted-foreground">
                      Hope Macy must register this account as a creditor account (email the Lynton team). Checkout
                      matches it by sort code and account number; run the connectivity diagnostic to check.
                    </p>
                    {slickpayCreditConfigured && (
                      <p className="rounded-md bg-green-50 px-3 py-2 text-xs text-green-700 border border-green-200">
                        Platform creditor account is configured. Enter new values below to update it.
                      </p>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="slickpayCreditName">Name on Account</Label>
                      <Input
                        id="slickpayCreditName"
                        value={slickpayCreditNameOnAccount}
                        onChange={(e) => setSlickpayCreditNameOnAccount(e.target.value)}
                        placeholder="e.g. Masonic Tools Ltd"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="slickpayCreditSortCodeInput">Sort Code (6 digits, no hyphens)</Label>
                        <Input
                          id="slickpayCreditSortCodeInput"
                          value={slickpayCreditSortCode}
                          onChange={(e) => setSlickpayCreditSortCode(e.target.value)}
                          placeholder="040004"
                          maxLength={8}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="slickpayCreditAccountNumberInput">Account Number (8 digits)</Label>
                        <Input
                          id="slickpayCreditAccountNumberInput"
                          value={slickpayCreditAccountNumber}
                          onChange={(e) => setSlickpayCreditAccountNumber(e.target.value)}
                          placeholder="00000000"
                          maxLength={8}
                        />
                      </div>
                    </div>
                    <Button type="submit" disabled={savingSlickpayCreditCreds}>
                      {savingSlickpayCreditCreds ? 'Saving…' : slickpayCreditConfigured ? 'Update platform bank account' : 'Save platform bank account'}
                    </Button>
                  </form>
                )}

                {creditsPaymentProvider === 'slickpay' && (
                  <div className="max-w-xl space-y-3 border rounded-lg p-4 mt-4">
                    <h3 className="text-sm font-semibold">Hope Macy IP whitelist</h3>
                    <p className="text-xs text-muted-foreground">
                      Register these egress IPs with Hope Macy so Cloud Functions can reach the PIS API.
                      VPC connector: <code className="text-xs">{slickpayNetwork.vpcConnectorName}</code>
                    </p>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      {slickpayNetwork.ipWhitelist.map((ip) => (
                        <li key={ip}>
                          <code className="text-xs">{ip}</code>
                          {ip === slickpayNetwork.expectedEgressIp ? (
                            <span className="text-muted-foreground"> (expected production egress)</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    <h3 className="text-sm font-semibold pt-2">Connectivity diagnostic</h3>
                    <p className="text-xs text-muted-foreground">
                      Runs from production Cloud Functions (same VPC egress and Secret Manager path as checkout).
                      Produces a report you can paste to Hope Macy support.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleRunSlickPayDiagnostic()}
                      disabled={diagnosingSlickpay}
                    >
                      {diagnosingSlickpay ? 'Running check…' : 'Run connectivity check'}
                    </Button>
                    {slickpayDiagnosticReport ? (
                      <div className="space-y-2">
                        <pre className="text-xs whitespace-pre-wrap break-all rounded-md border bg-muted/40 p-3 max-h-64 overflow-y-auto">
                          {slickpayDiagnosticReport}
                        </pre>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => void navigator.clipboard.writeText(slickpayDiagnosticReport)}
                        >
                          Copy report
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Methods Configuration */}
            <div>
              <h2 className="text-2xl font-bold mb-2">Payment Methods</h2>
              <p className="text-muted-foreground mb-6">
                Configure which payment methods are available platform-wide and set transaction fees for each method.
              </p>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Bank Transfer */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Building2 className="mr-2 h-5 w-5" />
                        Bank Transfer
                      </div>
                      <Switch
                        checked={bankTransferEnabled}
                        onCheckedChange={(checked) => handlePaymentMethodToggle('bankTransfer', checked)}
                      />
                    </CardTitle>
                    <CardDescription>
                      Direct bank transfers with manual verification
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="bankTransferFee">Transaction Fee (£)</Label>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold">£</span>
                        <Input
                          id="bankTransferFee"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={bankTransferFee}
                          onChange={(e) => setBankTransferFee(e.target.value)}
                          disabled={!bankTransferEnabled}
                          className="max-w-[120px]"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Typically £0.00 for bank transfers
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Stripe */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CreditCard className="mr-2 h-5 w-5" />
                        Stripe
                      </div>
                      <Switch
                        checked={stripeEnabled}
                        onCheckedChange={(checked) => handlePaymentMethodToggle('stripe', checked)}
                      />
                    </CardTitle>
                    <CardDescription>
                      Card payments via Stripe Connected Accounts
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="stripeFee">Transaction Fee (£)</Label>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold">£</span>
                        <Input
                          id="stripeFee"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={stripeFee}
                          onChange={(e) => setStripeFee(e.target.value)}
                          disabled={!stripeEnabled}
                          className="max-w-[120px]"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Platform fee for card processing
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* SumUp */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Smartphone className="mr-2 h-5 w-5" />
                        SumUp
                      </div>
                      <Switch
                        checked={sumupEnabled}
                        onCheckedChange={(checked) => handlePaymentMethodToggle('sumup', checked)}
                      />
                    </CardTitle>
                    <CardDescription>
                      Card payments via SumUp with unit API keys
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sumupFee">Transaction Fee (£)</Label>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold">£</span>
                        <Input
                          id="sumupFee"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={sumupFee}
                          onChange={(e) => setSumupFee(e.target.value)}
                          disabled={!sumupEnabled}
                          className="max-w-[120px]"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Platform fee for SumUp payments
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* SlickPay */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Wallet className="mr-2 h-5 w-5" />
                        SlickPay
                      </div>
                      <Switch
                        checked={slickpayEnabled}
                        onCheckedChange={(checked) => handlePaymentMethodToggle('slickpay', checked)}
                      />
                    </CardTitle>
                    <CardDescription>
                      Open Banking via Hope Macy PIS API v1: guests are redirected to a hosted payment portal
                      and returned via callback. Requires bank transfer details configured per unit.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="slickpayFee">Transaction Fee (£)</Label>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold">£</span>
                        <Input
                          id="slickpayFee"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={slickpayFee}
                          onChange={(e) => setSlickpayFee(e.target.value)}
                          disabled={!slickpayEnabled}
                          className="max-w-[120px]"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Platform fee for SlickPay Open Banking payments
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Save Button */}
              <div className="mt-6">
                <form onSubmit={handleSavePaymentFees}>
                  <Button type="submit" disabled={saving} size="lg">
                    {saving ? 'Saving...' : 'Save Payment Method Fees'}
                  </Button>
                </form>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Link Shortener Integration
                </CardTitle>
                <CardDescription>
                  Configure the ma-to bearer token used to generate short RSVP links.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveMaToToken} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="maToBearerToken">ma-to Bearer Token</Label>
                    <Input
                      id="maToBearerToken"
                      type="password"
                      autoComplete="new-password"
                      value={maToBearerToken}
                      onChange={(e) => setMaToBearerToken(e.target.value)}
                      placeholder="Enter bearer token"
                    />
                    <p className="text-xs text-muted-foreground">
                      Token is encrypted server-side and never stored in plain text.
                    </p>
                    {tokenConfigured && (
                      <p className="text-xs text-muted-foreground">
                        Configured token ending in <span className="font-mono">{tokenLastFour}</span>
                      </p>
                    )}
                  </div>
                  <Button type="submit" disabled={savingToken}>
                    {savingToken ? 'Saving...' : 'Save Token'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

